/**
 * Configuración de validación de imágenes (sincronizada con backend)
 */
export const IMAGE_CONFIG = {
  MAX_WIDTH: 2048,
  MAX_HEIGHT: 2048,
  MAX_FILE_SIZE: 2 * 1024 * 1024, // 2MB
  MAX_IMAGES: 10,
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/webp'],
  JPEG_QUALITY: 0.85,
} as const;

export interface ImageValidationError {
  field: string;
  message: string;
  fileName?: string;
  currentResolution?: string;
  maxResolution?: string;
  currentSize?: string;
  maxSize?: string;
}

export interface ImageValidationResult {
  valid: boolean;
  errors: ImageValidationError[];
  processedImage?: string; // base64 de imagen redimensionada
}

/**
 * Valida las dimensiones de una imagen
 */
export const validateImageDimensions = (
  width: number,
  height: number,
  fileName: string
): ImageValidationError | null => {
  if (width > IMAGE_CONFIG.MAX_WIDTH || height > IMAGE_CONFIG.MAX_HEIGHT) {
    return {
      field: 'image',
      message: `La imagen "${fileName}" excede la resolución máxima`,
      fileName,
      currentResolution: `${width}x${height}px`,
      maxResolution: `${IMAGE_CONFIG.MAX_WIDTH}x${IMAGE_CONFIG.MAX_HEIGHT}px`,
    };
  }
  return null;
};

/**
 * Valida el tamaño de un archivo
 */
export const validateImageSize = (
  size: number,
  fileName: string
): ImageValidationError | null => {
  if (size > IMAGE_CONFIG.MAX_FILE_SIZE) {
    const sizeMB = (size / 1024 / 1024).toFixed(2);
    const maxMB = (IMAGE_CONFIG.MAX_FILE_SIZE / 1024 / 1024).toFixed(2);

    return {
      field: 'image',
      message: `La imagen "${fileName}" es demasiado grande`,
      fileName,
      currentSize: `${sizeMB}MB`,
      maxSize: `${maxMB}MB`,
    };
  }
  return null;
};

/**
 * Valida el tipo MIME de un archivo
 */
export const validateImageType = (
  type: string,
  fileName: string
): ImageValidationError | null => {
  if (!IMAGE_CONFIG.ALLOWED_TYPES.includes(type as any)) {
    return {
      field: 'image',
      message: `El formato de "${fileName}" no está permitido`,
      fileName,
    };
  }
  return null;
};

/**
 * Estima el tamaño en bytes de un string base64
 */
export const estimateBase64Size = (base64: string): number => {
  // Remover header "data:image/...;base64," si existe
  const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;

  // Calcular tamaño: cada 4 caracteres base64 = 3 bytes
  // Ajustar por padding (caracteres '=')
  const padding = (base64Data.match(/=/g) || []).length;
  return (base64Data.length * 3) / 4 - padding;
};

/**
 * Redimensiona y comprime una imagen con ajuste dinámico de calidad
 * Intenta diferentes niveles de compresión hasta lograr el tamaño objetivo
 */
export const resizeAndCompressImage = (
  file: File,
  maxWidth: number,
  maxHeight: number,
  initialQuality: number,
  targetSize: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // Calcular nuevas dimensiones manteniendo aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No se pudo crear el contexto del canvas'));
          return;
        }

        // Dibujar imagen redimensionada
        ctx.drawImage(img, 0, 0, width, height);

        // Comprimir con ajuste dinámico de calidad
        let quality = initialQuality;
        let base64: string = '';
        const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

        // Intentar con diferentes niveles de calidad hasta que quepa
        for (let i = 0; i < 5; i++) {
          base64 = canvas.toDataURL(mimeType, quality);
          const estimatedSize = estimateBase64Size(base64);

          console.log(`Intento ${i + 1}: calidad=${quality.toFixed(2)}, tamaño=${(estimatedSize / 1024).toFixed(2)}KB`);

          if (estimatedSize <= targetSize || quality <= 0.5) {
            console.log(`✓ Compresión exitosa: ${(estimatedSize / 1024).toFixed(2)}KB con calidad ${quality.toFixed(2)}`);
            resolve(base64);
            return;
          }

          // Reducir calidad para el siguiente intento
          quality -= 0.15;
        }

        // Si después de 5 intentos no se logra, devolver la última versión
        resolve(base64);
      };

      img.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Redimensiona una imagen manteniendo el aspect ratio
 */
export const resizeImage = (
  file: File,
  maxWidth: number = IMAGE_CONFIG.MAX_WIDTH,
  maxHeight: number = IMAGE_CONFIG.MAX_HEIGHT,
  quality: number = IMAGE_CONFIG.JPEG_QUALITY
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // Calcular nuevas dimensiones manteniendo aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No se pudo crear el contexto del canvas'));
          return;
        }

        // Dibujar imagen redimensionada
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir a base64
        const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const base64 = canvas.toDataURL(mimeType, quality);

        resolve(base64);
      };

      img.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Valida y procesa una imagen
 * Si excede las dimensiones o peso, la optimiza automáticamente
 */
export const validateAndProcessImage = async (
  file: File
): Promise<ImageValidationResult> => {
  const errors: ImageValidationError[] = [];

  // Validar tipo
  const typeError = validateImageType(file.type, file.name);
  if (typeError) {
    errors.push(typeError);
    return { valid: false, errors };
  }

  try {
    // Obtener dimensiones
    const dimensions = await getImageDimensions(file);

    let processedBase64: string;
    let needsResize = false;
    let needsCompression = false;

    // Verificar si necesita redimensionamiento
    if (dimensions.width > IMAGE_CONFIG.MAX_WIDTH ||
        dimensions.height > IMAGE_CONFIG.MAX_HEIGHT) {
      needsResize = true;
      console.warn(`La imagen "${file.name}" excede dimensiones: ${dimensions.width}x${dimensions.height}px`);
    }

    // Verificar si necesita compresión
    if (file.size > IMAGE_CONFIG.MAX_FILE_SIZE) {
      needsCompression = true;
      console.warn(`La imagen "${file.name}" excede peso: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    }

    // Procesar imagen si necesita optimización
    if (needsResize || needsCompression) {
      console.log(`🔄 Optimizando "${file.name}"...`);

      // Redimensionar Y comprimir en un solo paso
      processedBase64 = await resizeAndCompressImage(
        file,
        needsResize ? IMAGE_CONFIG.MAX_WIDTH : dimensions.width,
        needsResize ? IMAGE_CONFIG.MAX_HEIGHT : dimensions.height,
        IMAGE_CONFIG.JPEG_QUALITY, // 0.85 inicial
        IMAGE_CONFIG.MAX_FILE_SIZE
      );

      // Verificar que la compresión fue exitosa
      const compressedSize = estimateBase64Size(processedBase64);

      if (compressedSize > IMAGE_CONFIG.MAX_FILE_SIZE) {
        errors.push({
          field: 'image',
          message: `No se pudo comprimir "${file.name}" lo suficiente`,
          fileName: file.name,
          currentSize: `${(compressedSize / 1024 / 1024).toFixed(2)}MB`,
          maxSize: `${(IMAGE_CONFIG.MAX_FILE_SIZE / 1024 / 1024).toFixed(2)}MB`,
        });
        return { valid: false, errors };
      }

      console.log(`✅ Imagen optimizada: ${file.name}`);
      console.log(`   Original: ${(file.size / 1024).toFixed(2)}KB → Optimizada: ${(compressedSize / 1024).toFixed(2)}KB`);
      console.log(`   Reducción: ${((1 - compressedSize / file.size) * 100).toFixed(1)}%`);
    } else {
      // Sin optimización necesaria, convertir a base64 directamente
      processedBase64 = await fileToBase64(file);
    }

    return {
      valid: true,
      errors: [],
      processedImage: processedBase64,
    };
  } catch (error) {
    errors.push({
      field: 'image',
      message: `Error al procesar "${file.name}": ${error instanceof Error ? error.message : 'Error desconocido'}`,
      fileName: file.name,
    });

    return { valid: false, errors };
  }
};

/**
 * Obtiene las dimensiones de una imagen
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Convierte un File a base64
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Valida múltiples imágenes
 */
export const validateMultipleImages = async (
  files: File[]
): Promise<{
  valid: boolean;
  errors: ImageValidationError[];
  processedImages: string[];
}> => {
  if (files.length > IMAGE_CONFIG.MAX_IMAGES) {
    return {
      valid: false,
      errors: [{
        field: 'images',
        message: `Máximo ${IMAGE_CONFIG.MAX_IMAGES} imágenes permitidas. Has seleccionado ${files.length}`,
      }],
      processedImages: [],
    };
  }

  const results = await Promise.all(
    files.map(file => validateAndProcessImage(file))
  );

  const allErrors: ImageValidationError[] = [];
  const processedImages: string[] = [];

  results.forEach((result, index) => {
    if (!result.valid) {
      allErrors.push(...result.errors);
    } else if (result.processedImage) {
      processedImages.push(result.processedImage);
    }
  });

  return {
    valid: allErrors.length === 0,
    errors: allErrors,
    processedImages,
  };
};

/**
 * Comprime una imagen base64 (útil para previews generados con html2canvas)
 * Reduce el tamaño objetivo para previews de productos
 * @param base64Image - Imagen en formato base64
 * @param targetSizeKB - Tamaño objetivo en KB (default: 100KB para previews)
 * @param maxWidth - Ancho máximo (default: 800px para previews)
 * @param maxHeight - Alto máximo (default: 800px para previews)
 * @returns Promise<string> - Imagen comprimida en base64
 */
export const compressBase64Image = (
  base64Image: string,
  targetSizeKB: number = 100,
  maxWidth: number = 800,
  maxHeight: number = 800
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      // Calcular nuevas dimensiones manteniendo aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No se pudo crear el contexto del canvas'));
        return;
      }

      // Dibujar imagen redimensionada
      ctx.drawImage(img, 0, 0, width, height);

      // Comprimir con ajuste dinámico de calidad
      const targetSize = targetSizeKB * 1024; // Convertir a bytes
      let quality = 0.8; // Empezar con calidad 80%
      let base64: string = '';

      // Intentar con diferentes niveles de calidad hasta lograr el tamaño objetivo
      for (let i = 0; i < 6; i++) {
        // Usar JPEG para mejor compresión en previews
        base64 = canvas.toDataURL('image/jpeg', quality);
        const estimatedSize = estimateBase64Size(base64);

        console.log(`🔧 Compresión preview - Intento ${i + 1}: calidad=${quality.toFixed(2)}, tamaño=${(estimatedSize / 1024).toFixed(2)}KB`);

        // Si logramos el tamaño objetivo o la calidad es muy baja, terminamos
        if (estimatedSize <= targetSize || quality <= 0.4) {
          console.log(`✅ Preview comprimido: ${(estimatedSize / 1024).toFixed(2)}KB con calidad ${quality.toFixed(2)}`);
          resolve(base64);
          return;
        }

        // Reducir calidad para el siguiente intento
        quality -= 0.1;
      }

      // Si después de 6 intentos no se logra, devolver la última versión
      console.warn(`⚠️  No se alcanzó el tamaño objetivo de ${targetSizeKB}KB, usando ${(estimateBase64Size(base64) / 1024).toFixed(2)}KB`);
      resolve(base64);
    };

    img.onerror = () => {
      reject(new Error('Error al cargar la imagen base64'));
    };

    img.src = base64Image;
  });
};
